import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes, useTheme } from 'styled-components';
// [ONE GLASS RECIPE 2026-09-01, user] One tooltip surface for the whole app — see TipGlass.
import { GLASS_BLUR, GLASS_RADIUS, glassBackground, glassBorder, glassPointer, glassShadow } from '../components/TipGlass';
import { fetchNewSales, fetchNewSalesFilters, fetchNewSalesSummary, peekCache, monthsParams } from '../services/api';
import { useRestorableState } from '../services/uiSnapshot'; // [SEAMLESS-UPDATE 2026-08-13]
import { useRealtime } from '../hooks/useRealtime'; // [REALTIME] 2026-06-23
import { NewSaleRow, NewSalesSummary, SortConfig, Column, NewSalesFiltersResponse, MergedPaymentBrief, NewSalesRefundDispute } from '../types';
import SearchBar from '../components/SearchBar';
import DataTable from '../components/DataTable';
import { useAuth } from '../contexts/AuthContext'; // [COL-RESIZE-ALL 2026-09-29] keys the remembered column widths
import { SkeletonBox } from '../components/Skeleton';
import { RefundBreakdownCard, useRefundBreakdownHover } from '../components/RefundBreakdownCard'; // [NEW SALES BREAKDOWN 2026-09-17]
import GlassSelect from '../components/GlassSelect';
import { effectiveCurrentMonth } from '../utils/effectiveMonth';

/* [PERF 2026-08-14] Locale formatters built ONCE — `toLocale*(locale, {options})`
 * constructs a fresh Intl formatter per call (measured 34us money / 90us date on V8,
 * vs 0.7us / 1.5us cached). Calls WITHOUT options are already fast and left alone. */
const MONEY_FMT = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const MONTH_LONG_YEAR_FMT = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' });
// [DATE-STYLE 2026-09-29, user] One date style across the app: "Sep 8, 2026" — the hover card's short
// form gains the year too, so every date on screen reads the same way.
const MONTH_SHORT_DAY_UTC_FMT = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
const MONTH_SHORT_DAY_YEAR_UTC_FMT = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });

import { useIsMobile } from '../hooks/useIsMobile'; // [MOBILE 2026-07-07]
import { pageRect } from '../utils/appZoom'; // [APP-FIT 2026-10-26] tips placed in page px

const Container = styled.div`
  display: flex;
  flex-direction: column;
  /* [STICKY-HEAD] Fill the scroll card exactly and don't scroll HERE — the toolbar
     + summary strip stay fixed and only the table area (below) scrolls. Mirrors the
     Payments page scaffold. */
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background: ${(p) => p.theme.surface};

  /* [PHONE-LAYOUT 2026-10-23, user] phones: NOTHING is fixed at the top — the whole page (title, search, filters, the
     USA + ROW bars, the table) is ONE scroll, as Payments ("in the phone view nothing will be fixed on the top side
     otherwise how will i see the data for the below"). The table only swipes sideways (TableScroll below). */
  @media (max-width: 768px) {
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior-y: contain;
    -webkit-overflow-scrolling: touch;
  }
`;

// [STICKY-HEAD] The FIXED top block — the search toolbar + the New Sales summary
// strip. It sits OUTSIDE the scrolling table area (flex-shrink:0) so it never
// scrolls; only the table rows below it do.
const FrozenTop = styled.div`
  flex-shrink: 0;
  background: ${(p) => p.theme.surface};
`;

// [STICKY-HEAD] The scrolling table region — owns the vertical (and horizontal on
// narrow screens) scroll. The DataTable's sticky <thead> pins to the top of THIS
// area (via parentScroll), so the column header stays just under the frozen block
// while rows scroll. padding-bottom clears the floating dock at the very end of the
// scroll so the last row never hides behind the bottom pill.
const TableScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-x: auto;
  overflow-y: scroll; /* [SCROLLBAR 2026-07-08] always show the vertical scroller on desktop */
  padding-bottom: 88px;

  /* [PHONE-LAYOUT 2026-10-23, user] Phones: as tall as its rows (no box of its own to scroll up / down in): the table
     only swipes sideways here, the page scrolls (Container). The 88 px end pad still lifts the last row above the
     dock. The column header scrolls away with the page, as on Payments. */
  @media (max-width: 768px) {
    overscroll-behavior-x: contain;
    -webkit-overflow-scrolling: touch;
    flex: none;
    min-height: 0;
    overflow-y: hidden;
    overscroll-behavior-y: auto;
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: ${(p) => (p.theme.colorScheme === 'dark'
    ? 'linear-gradient(180deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.04) 100%)'
    : 'linear-gradient(180deg, rgba(241,245,249,0.85) 0%, rgba(229,232,231,0.72) 100%)')};
  /* No backdrop-filter — it flashed white during AppLayout's PageFade tab
     transition (Chromium glitch) and the blur did nothing over the solid card. */
  border-bottom: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(22,25,23,0.10)')};
  box-shadow: inset 0 1px 0 ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.55)')};
  flex-wrap: wrap;
  gap: 10px;

  /* Phones: stack title / search / filters vertically (each full-width). */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
  @media (max-width: 600px) {
    padding: 10px 12px;
  }
`;

const PageTitle = styled.h1`
  font-size: 16px;
  font-weight: 700;
  color: ${(p) => p.theme.text};
  margin: 0;

  /* [NEW-SALES PHONE TITLE 2026-09-23, user] "new sale heading will come in middle … in phone".
     Phones stack the toolbar full-width (see Toolbar), so centring the text centres the title;
     the search and filter rows below keep their layout. Desktop unchanged. */
  @media (max-width: 768px) {
    text-align: center;
  }
`;

const Filters = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  /* [MOBILE 2026-07-07] Phones: the two filter dropdowns (Month + Sales Rep) share
     ONE row as an equal 2-column grid on their own row below the search (see the
     render branch). min-width:0 lets the glass triggers shrink below their 130px
     desktop floor so both fit the phone width. Mirrors the Refunds page. */
  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: 100%;
    gap: 8px;
    & > button { min-width: 0; width: 100%; }
  }
`;

const RecordCount = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${(p) => p.theme.muted};
  white-space: nowrap;
`;

// [MOBILE 2026-07-07] Phones only: the search field and the record count share one
// row (count pinned to the right by the inline search's flex:1 growth), with the two
// filter dropdowns on the row below. Desktop keeps search in the middle + count beside
// the filters. Mirrors the Refunds page.
const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
`;

const FilterSelect = GlassSelect;

// ── Summary strip — same family as the Payments page's In Bank Summary ──
const SummarySection = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid ${(p) => p.theme.line};
`;

/* [NEW SALES USA/ROW 2026-09-17, user] "just like in the payment page … the data for the usa and row
   separate": the strip is now TWO stacked bars, USA on top and ROW below, each opening with its name
   in a narrow cell — the Payments In Bank Summary's layout and colours (InBank2Stack /
   InBank2ScopeCell: grey = USA, violet = Team ROW). */
const SummaryStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  /* [PHONE-LAYOUT 2026-10-23] Phones: ONE sideways scroll for both bars, as the Payments In Bank Summary ([ONE-SCROLL
     2026-09-28, user] "in phone view it has 3 scrolls, make it to one scroll only"): each bar swiped on its own, so
     the USA and ROW figures drifted out of line. Now the stack swipes and both bars move together, column for
     column (each bar's width is set on SummaryGrid). */
  @media (max-width: 768px) {
    overflow-x: auto;
    overflow-y: hidden;
    overscroll-behavior-x: contain;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 4px;
  }
`;

type SalesScope = 'usa' | 'row';
const SALES_SCOPES: SalesScope[] = ['usa', 'row'];
const SALES_SCOPE_LABEL: Record<SalesScope, string> = { usa: 'USA', row: 'ROW' };

const ScopeCell = styled.div<{ $scope: SalesScope }>`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 10px 8px;
  border-right: 1px solid ${(p) => p.theme.line};
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  white-space: nowrap;
  /* [PHONE-LAYOUT 2026-10-22] Phones: 11 px (the phone minimum for labels). */
  @media (max-width: 768px) { font-size: 11px; }
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.035)' : 'rgba(22,25,23,0.03)')};
  color: ${(p) => (p.$scope === 'row'
    ? (p.theme.colorScheme === 'dark' ? '#c4b5fd' : '#6d28d9')
    : p.theme.muted)};
`;

const SummaryGrid = styled.div<{ $cols?: number; $labelCol?: boolean }>`
  display: grid;
  /* [RESPONSIVE 2026-07-07] Was repeat($cols,1fr) + overflow:hidden, which forced
     the fixed columns to shrink until the nowrap labels overflowed and were
     SILENTLY clipped at the right edge on narrow screens (no scroll, no wrap).
     Mirror the Payments In-Bank strip (InBank2Grid): lay cells out at a ~132px
     floor and scroll horizontally when they don't all fit instead of clipping.
     $cols is kept for caller API compatibility but no longer drives the layout. */
  grid-auto-flow: column;
  grid-auto-columns: minmax(132px, 1fr);
  /* [NEW SALES USA/ROW 2026-09-17] One narrow explicit track for the USA / ROW name, as Payments. */
  grid-template-columns: ${(p) => (p.$labelCol ? '78px' : 'none')};
  gap: 0;
  border: 1px solid ${(p) => p.theme.line};
  border-radius: 8px;
  overflow-x: auto;
  overflow-y: hidden;

  /* [PHONE-LAYOUT 2026-10-23] Phones: the bar no longer scrolls by itself (SummaryStack is the one scroll area). Both
     bars get the SAME width — the name cell plus the 132 px floor per figure, never narrower than the card — so they
     line up column for column (Payments' InBank2Grid rule). The +2 px is the 1 px left and right border. */
  @media (max-width: 768px) {
    width: calc(${(p) => (p.$labelCol ? 78 : 0)}px + ${(p) => p.$cols ?? 0} * 132px + 2px);
    min-width: 100%;
    overflow: hidden;
  }
`;

const SummaryCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px 8px;
  border-right: 1px solid ${(p) => p.theme.line};
  text-align: center;

  &:last-child {
    border-right: none;
  }
`;

const SummaryLabel = styled.span`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${(p) => p.theme.muted};
  margin-bottom: 4px;
  /* [RESPONSIVE 2026-07-07] Was nowrap (clipped the longer labels when squeezed);
     wrap to a second line inside the floored cell, matching InBank2Label. */
  white-space: normal;
  line-height: 1.2;
  /* [PHONE-LAYOUT 2026-10-22] Phones: 11 px labels (they already wrap to a second line). */
  @media (max-width: 768px) { font-size: 11px; }
`;

// [NET-IN-BANK NEW SALES 2026-09-16] $tone: red for money out (Refunds / Disputes, a negative Net
// In-Bank), green for money kept (Disputes Won) — the same colours My Sales uses for those tiles.
const SummaryValue = styled.span<{ $accent?: boolean; $tone?: 'red' | 'green' }>`
  font-size: 15px;
  font-weight: 700;
  color: ${(p) => (p.$tone === 'red'
    ? (p.theme.colorScheme === 'dark' ? '#f87171' : '#dc2626')
    : p.$accent || p.$tone === 'green'
      ? (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)
      : p.theme.text)};
`;

// "New" / "Existing" customer pill, shown in the PM Name cell.
const CustomerPill = styled.span<{ $new?: boolean }>`
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.4px;
  border-radius: 5px;
  padding: 2px 7px;
  white-space: nowrap;
  /* [PHONE-LAYOUT 2026-10-22] */
  @media (max-width: 768px) { font-size: 11px; }
  ${(p) => p.$new
    ? `color: ${p.theme.colorScheme === 'dark' ? '#fde68a' : '#92400e'};
       background: rgba(245, 158, 11, 0.16);
       border: 1px solid rgba(245, 158, 11, 0.45);`
    : `color: ${p.theme.muted};
       background: ${p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(22,25,23,0.05)'};
       border: 1px solid ${p.theme.line};`}
`;

function fmtMoney(n: number | null | undefined): string {
  const v = Number(n) || 0;
  // [NET-IN-BANK NEW SALES 2026-09-16] Sign before the $ — Net In-Bank can go negative.
  return `${Math.round(v * 100) < 0 ? '-' : ''}$${MONEY_FMT.format(Math.abs(v))}`;
}

// Short date for the merged-payments hover card (rows share a month, so no year).
function fmtBriefDate(val: string | null): string {
  if (!val) return '—';
  return MONTH_SHORT_DAY_UTC_FMT.format(new Date(val));
}

// Money cell that only "lights up" when an amount was actually marked, so the
// row visibly shows which bucket(s) the sale was booked into.
function MoneyCell({ value, accent }: { value: number | null | undefined; accent?: boolean }) {
  const dark = useTheme().colorScheme === 'dark'; // [GRAPHITE-EMERALD] before the early return (hook order)
  const v = Number(value) || 0;
  if (v === 0) return <span style={{ opacity: 0.35 }}>—</span>;
  return <span style={{ fontWeight: accent ? 700 : 600, color: accent ? (dark ? '#44b57b' : '#13b790') : undefined }}>{fmtMoney(v)}</span>;
}

// ── "Existing client" hover tooltip ───────────────────────────────────────
// Premium glass card surfacing the row's PM + Placed Value. Portaled to <body>
// so the table's overflow-x can't clip it, and positioned with fixed coords from
// the pill's bounding box (flips below the pill when there isn't room above).
const tipInTop = keyframes`
  from { opacity: 0; transform: translate(-50%, calc(-100% - 4px)); }
  to   { opacity: 1; transform: translate(-50%, calc(-100% - 12px)); }
`;
const tipInBottom = keyframes`
  from { opacity: 0; transform: translate(-50%, 4px); }
  to   { opacity: 1; transform: translate(-50%, 12px); }
`;

const Tooltip = styled.div<{ $below?: boolean }>`
  position: fixed;
  z-index: 1200;
  pointer-events: none;
  min-width: 184px;
  padding: 11px 14px;
  /* [ONE GLASS RECIPE 2026-09-01, user] "make the same tooltip design, all over the application."
     Surface from the shared recipe in components/TipGlass, exactly as My Sales' card does. Only
     the surface is shared — the size, padding and the flip-aware notch stay this page's own. */
  border-radius: ${GLASS_RADIUS}px;
  background: ${(p) => glassBackground(p.theme.colorScheme === 'dark')};
  backdrop-filter: ${GLASS_BLUR};
  -webkit-backdrop-filter: ${GLASS_BLUR};
  border: ${(p) => glassBorder(p.theme.colorScheme === 'dark')};
  box-shadow: ${(p) => glassShadow(p.theme.colorScheme === 'dark')};
  animation: ${(p) => (p.$below ? tipInBottom : tipInTop)} 0.16s cubic-bezier(0.16, 1, 0.3, 1) both;

  /* Notch pointing back at the pill. */
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    width: 11px;
    height: 11px;
    transform: translateX(-50%) rotate(45deg);
    background: ${(p) => glassPointer(p.theme.colorScheme === 'dark')};
    ${(p) => {
      const edge = glassBorder(p.theme.colorScheme === 'dark');
      return p.$below
        ? `top: -5px;
         border-left: ${edge};
         border-top: ${edge};`
        : `bottom: -5px;
         border-right: ${edge};
         border-bottom: ${edge};`;
    }}
  }
`;

const TipRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  & + & { margin-top: 8px; }
`;

const TipLabel = styled.span`
  font-size: 9.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${(p) => p.theme.muted};
`;

const TipValue = styled.span<{ $accent?: boolean }>`
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${(p) => (p.$accent ? (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600) : p.theme.text)};
`;

// Title line of the merged-payments card (e.g. "2 PAYMENTS").
const TipHeader = styled.div`
  font-size: 9.5px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  color: ${(p) => p.theme.muted};
  margin-bottom: 9px;
  padding-bottom: 7px;
  border-bottom: 1px solid ${(p) => p.theme.line};
`;

// Per-payment date in the card (not uppercase — it's a real date).
const TipDate = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${(p) => p.theme.muted};
  font-variant-numeric: tabular-nums;
`;

const TipDivider = styled.div`
  height: 1px;
  background: ${(p) => p.theme.line};
  margin: 9px 0 8px;
`;

// "(N)" count chip after a clubbed website. Soft green tint to read as
// "this site rolls up multiple payments"; lifts subtly on hover.
const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 6px;
  min-width: 19px;
  height: 18px;
  padding: 0 6px;
  font-size: 10.5px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  line-height: 1;
  border-radius: 9px;
  /* [PHONE-LAYOUT 2026-10-22] */
  @media (max-width: 768px) { font-size: 11px; height: 20px; border-radius: 10px; }
  cursor: default;
  vertical-align: middle;
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#7de5ab' : '#1e765e')};
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(68,181,123,0.16)' : 'rgba(19,183,144,0.12)')};
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(68,181,123,0.38)' : 'rgba(19,183,144,0.34)')};
  transition: background 120ms cubic-bezier(0.4, 0, 0.2, 1), border-color 120ms cubic-bezier(0.4, 0, 0.2, 1), transform 120ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 120ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(68,181,123,0.26)' : 'rgba(19,183,144,0.20)')};
    border-color: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(68,181,123,0.6)' : 'rgba(19,183,144,0.5)')};
    transform: translateY(-1px);
    box-shadow: 0 3px 10px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(68,181,123,0.22)' : 'rgba(19,183,144,0.20)')};
  }
`;

const WebsiteText = styled.span`
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
`;

const ExistingClientTag: React.FC<{ pmName: string; placedValue: number | null | undefined }> = ({ pmName, placedValue }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [tip, setTip] = useState<{ x: number; y: number; below: boolean } | null>(null);

  const show = () => {
    const el = ref.current;
    if (!el) return;
    const r = pageRect(el); // [APP-FIT 2026-10-26] page px, like the tip's left / top
    const below = r.top < 140; // not enough headroom → drop the card below the pill
    setTip({ x: r.left + r.width / 2, y: below ? r.bottom : r.top, below });
  };
  const hide = () => setTip(null);

  return (
    <>
      <CustomerPill ref={ref} onMouseEnter={show} onMouseLeave={hide} style={{ cursor: 'default' }}>
        Existing client
      </CustomerPill>
      {tip && createPortal(
        <Tooltip $below={tip.below} style={{ left: tip.x, top: tip.y }}>
          <TipRow>
            <TipLabel>PM</TipLabel>
            <TipValue>{pmName}</TipValue>
          </TipRow>
          <TipRow>
            <TipLabel>Placed Value</TipLabel>
            <TipValue $accent>{placedValue == null ? '—' : fmtMoney(placedValue)}</TipValue>
          </TipRow>
        </Tooltip>,
        document.body
      )}
    </>
  );
};

// Website cell for a CLUBBED row: shows "site (N)" and, on hovering the chip,
// reveals a premium glass card listing each underlying payment (date + amount)
// with the rolled-up total. Same portal/positioning approach as ExistingClientTag
// so the table's overflow can't clip the card.
const MergedWebsiteCell: React.FC<{ label: string; count: number; merged: MergedPaymentBrief[] }> = ({ label, count, merged }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [tip, setTip] = useState<{ x: number; y: number; below: boolean } | null>(null);

  const show = () => {
    const el = ref.current;
    if (!el) return;
    const r = pageRect(el); // [APP-FIT 2026-10-26] page px, like the tip's left / top
    const below = r.top < 160; // not enough headroom → drop the card below the chip
    setTip({ x: r.left + r.width / 2, y: below ? r.bottom : r.top, below });
  };
  const hide = () => setTip(null);

  const total = merged.reduce((s, m) => s + (Number(m.amount) || 0), 0);

  return (
    <WebsiteText>
      {label}
      <CountBadge ref={ref} onMouseEnter={show} onMouseLeave={hide}>{count}</CountBadge>
      {tip && createPortal(
        <Tooltip $below={tip.below} style={{ left: tip.x, top: tip.y }}>
          <TipHeader>{count} payments</TipHeader>
          {merged.map((m) => (
            <TipRow key={m.id}>
              <TipDate>{fmtBriefDate(m.payment_date)}</TipDate>
              <TipValue>{fmtMoney(m.amount)}</TipValue>
            </TipRow>
          ))}
          <TipDivider />
          <TipRow>
            <TipLabel>Total</TipLabel>
            <TipValue $accent>{fmtMoney(total)}</TipValue>
          </TipRow>
        </Tooltip>,
        document.body
      )}
    </WebsiteText>
  );
};

// [EFFECTIVE-MONTH 2026-07-01] Use the app-wide effective current month (12:30 PM
// IST rollover on the 1st) so the default month matches the rest of the app instead
// of flipping at local midnight.
function getCurrentMonth(): string {
  return effectiveCurrentMonth();
}

// [MONTH-FULL-NAME] 2026-06-23 — month dropdowns show the full month name
// (e.g. "June 2026") to match the EOM page. This label feeds only the filter
// dropdown, so 'long' is safe everywhere it's used.
function formatMonthLabel(m: string): string {
  const [year, month] = m.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1);
  return MONTH_LONG_YEAR_FMT.format(date);
}

const baseColumns: Omit<Column, 'render'>[] = [
  { key: 'payment_date', label: 'Payment Date', sortable: true },
  { key: 'website', label: 'Website', sortable: true, breakable: true },
  { key: 'email', label: 'Email', sortable: true, breakable: true },
  { key: 'amount', label: 'Amount', sortable: true, align: 'center' as const },
  { key: 'account_name', label: 'Account', sortable: true },
  { key: 'sales_rep', label: 'Sales Rep', sortable: true },
  { key: 'pm_name', label: 'Client Status', sortable: true, align: 'center' as const },
  { key: 'recurring_new_sale', label: 'Recurring New Sale', sortable: true, align: 'center' as const },
  { key: 'one_time_new_sale', label: 'One-Time New Sale', sortable: true, align: 'center' as const },
  { key: 'sales_pia', label: 'Sales PIA', sortable: true, align: 'center' as const },
  { key: 'total_payment_received', label: 'Total Payment Received', sortable: true, align: 'center' as const },
];

const NewSalesPage: React.FC = () => {
  const { user } = useAuth(); // [COL-RESIZE-ALL 2026-09-29]
  const isMobile = useIsMobile(); // [MOBILE 2026-07-07] drives the toolbar reflow + desktop table on phones
  const [data, setData] = useState<NewSaleRow[]>([]);
  const [sort, setSort] = useRestorableState<SortConfig>('newsales.sort', { sortBy: 'payment_date', sortOrder: 'desc' });
  const [search, setSearch] = useRestorableState('newsales.search', '');
  // [MULTI-MONTH 2026-07-15] Multi-select month filter. `month` stays derived (the
  // single-month value, '' when 0 or 2+ are picked) so existing single-month
  // consumers keep working; `monthsKey` is the stable effect dependency — `month`
  // can't distinguish [Jul,Jun] from [Jul,May] and would skip the refetch.
  const [months, setMonths] = useRestorableState<string[]>('newsales.months', [getCurrentMonth()]);
  const month = months.length === 1 ? months[0] : '';
  const monthsKey = months.join(',');
  const [account, setAccount] = useRestorableState('newsales.account', '');
  // [SALES-REP-FILTER 2026-07-02] Toolbar rep dropdown — client-side narrow over the
  // loaded rows (the endpoint already returns only this user's in-scope set). '' = show
  // the whole in-scope set (own data for a rep, the team for a lead, everyone for admin).
  // [NEWSALES-SCOPE 2026-07-06] The default selection + the option list now come from the
  // server scope (see the filters load below), so a rep lands on their own name.
  const [salesRep, setSalesRep] = useState('');
  const [filters, setFilters] = useState<NewSalesFiltersResponse>({ accounts: [], months: [], salesReps: [], defaultRep: '', canSelectAll: false });
  // Apply the server's default rep exactly once (so a later user pick isn't clobbered).
  const repInitRef = useRef(false);
  const [summary, setSummary] = useState<NewSalesSummary>({
    recurring_new_sale: 0, one_time_new_sale: 0, sales_pia: 0, recovery: 0,
    total_payment_received: 0, records: 0, new_customers: 0,
    refunds_disputes: 0, disputes_won: 0, refunds_breakdown: [], won_breakdown: [],
  });
  // [LOAD-STATES 2026-09-29, user] A failed load left the zeros above on screen ($0.00 everywhere).
  // Now the boxes show "—" and the table shows the "Couldn't load new sales" countdown card.
  const [summaryFailed, setSummaryFailed] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  // [NEW SALES BREAKDOWN 2026-09-17, user] Hover list on the Refunds / Disputes + Disputes Won boxes,
  // the same card and timing as My Inventory.
  const breakdownHover = useRefundBreakdownHover();
  const [loading, setLoading] = useState(true);
  const silentRef = useRef(false);
  const loadRequestIdRef = useRef(0);

  // Load filter options + the caller's rep scope once. Seed the rep dropdown to the
  // server default (own name for a rep/lead; '' = All for admin/senior) the first time.
  useEffect(() => {
    fetchNewSalesFilters()
      .then((f) => {
        setFilters(f);
        if (!repInitRef.current) {
          setSalesRep(f.defaultRep);
          repInitRef.current = true;
        }
      })
      .catch((err) => console.error('Failed to load New Sales filters:', err));
  }, []);

  const loadSummary = useCallback(() => {
    fetchNewSalesSummary({ months: months.length ? months : undefined, account: account || undefined })
      .then((s) => { setSummary(s); setSummaryFailed(false); })
      .catch((err) => { console.error('Failed to load New Sales summary:', err); setSummaryFailed(true); }); // [LOAD-STATES 2026-09-29]
    // monthsKey, not `month` — see its definition (can't tell one multi-selection
    // from another, so the summary would go stale).
  }, [monthsKey, account]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  // [LOAD-STATES 2026-09-29, user] A failed summary retries by itself every 10 s (paused while the
  // browser tab is hidden), so its "—" boxes fill in as soon as the server answers again.
  const loadSummaryRef = useRef(loadSummary);
  loadSummaryRef.current = loadSummary;
  useEffect(() => {
    if (!summaryFailed) return undefined;
    const id = window.setInterval(() => { if (!document.hidden) loadSummaryRef.current(); }, 10000);
    return () => window.clearInterval(id);
  }, [summaryFailed]);

  const load = useCallback(async () => {
    const reqId = ++loadRequestIdRef.current;
    // [TAB-FAST 2026-07-07] Seed instantly from the SWR GET cache (a prior visit — New Sales
    // data isn't idle-prefetched) so a warmed revisit paints its rows at once with NO skeleton
    // flash. Mirrors fetchNewSales's params exactly so the key lines up; the fetch below still
    // runs and revalidates. A cache miss is a safe no-op (falls back to the skeleton).
    const seed = silentRef.current ? undefined : peekCache<{ data: NewSaleRow[] }>('/new-sales', monthsParams({ account: account || undefined, months: months.length ? months : undefined, search: search || undefined }));
    if (!silentRef.current) {
      if (seed) { setData(seed.data); setLoading(false); }
      else setLoading(true);
    }
    try {
      const res = await fetchNewSales({
        account: account || undefined,
        months: months.length ? months : undefined,
        search: search || undefined,
      });
      // Superseded load: don't clear `loading` (that would flash "No records
      // found" before the winning load fills the table). The winner clears it.
      if (reqId !== loadRequestIdRef.current) return;
      setData(res.data);
      setLoadFailed(false); // [LOAD-STATES 2026-09-29]
      setLoading(false);
    } catch (err) {
      if (reqId !== loadRequestIdRef.current) return;
      if (!silentRef.current) console.error('Failed to load New Sales:', err);
      // [LOAD-STATES 2026-09-29, user] Quiet refreshes keep the screen; a real failed load shows the
      // countdown card unless a cached copy of these filters is already showing.
      if (!silentRef.current) { setLoadFailed(true); if (!seed) setData([]); }
      setLoading(false);
    }
    // monthsKey, not `month` — `month` is '' for ANY multi-selection, so it cannot
    // tell [Jul,Jun] from [Jul,May] and the table would silently never refetch.
  }, [account, monthsKey, search]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 5 minutes (silent — no spinner), mirroring Payments.
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.hidden) return; // idle/background tab shouldn't refetch
      silentRef.current = true;
      load().finally(() => { silentRef.current = false; });
      loadSummary();
    }, 300000);
    return () => clearInterval(interval);
  }, [load, loadSummary]);

  /* [REALTIME] 2026-06-23 — refresh when new-sales (payments/inventory) change.
   *
   * ⚠ [FLASH-FIX 2026-08-27] `silentRef` MUST be set, and this call is why. The comment here
   * always claimed "silently", but `load()` was called bare: `silentRef` stayed false, so load()
   * took the non-silent path and called setLoading(true) — the whole page dropped to its
   * skeleton. And the seed that normally prevents that cannot help here: services/realtime.ts
   * invalidates the GET cache for the changed datasets BEFORE it notifies subscribers, so
   * peekCache('/new-sales') is guaranteed to miss on exactly this path.
   *
   * `newSales` depends on payments AND inventory, so on a working day it bumps every few
   * seconds (a Stripe sync insert, anyone mapping a payment, the inventory reconciler) — the
   * user, watching prod: "again and again it refreshes the complete page". Every other page's
   * realtime handler already passes a silent flag; New Sales was the only one that did not.
   *
   * Silent = fetch underneath and swap the rows in when they land. Nothing flashes. */
  useRealtime(['newSales'], () => {
    silentRef.current = true;
    load().finally(() => { silentRef.current = false; });
    loadSummary();
  });
  // [NET-IN-BANK NEW SALES 2026-09-16] A refund/dispute edit (team, name, status, amount) moves the
  // three new boxes but no table row, so it refreshes the summary only.
  useRealtime(['refunds', 'disputes'], () => { loadSummary(); });

  const handleSort = (column: string) => {
    setSort((prev) => ({
      sortBy: column,
      sortOrder: prev.sortBy === column && prev.sortOrder === 'desc' ? 'asc' : 'desc',
    }));
  };

  // [NEWSALES-SCOPE 2026-07-06] The rep dropdown lists exactly who this user may see —
  // straight from the server scope (self / self+team / everyone) — so it's stable even
  // for a month with no rows. A still-selected rep stays visible if a refresh drops them.
  const repList = salesRep && !filters.salesReps.includes(salesRep)
    ? [salesRep, ...filters.salesReps]
    : filters.salesReps;

  // Rows after the sales-rep filter (a row matches if the rep is one of its deal owners).
  const filteredRows = useMemo(() => (
    salesRep
      ? data.filter((r) => String((r as any).sales_rep || '').split(',').map((s) => s.trim()).includes(salesRep))
      : data
  ), [data, salesRep]);

  // Records + summary tiles reflect the rep filter: server summary when All, else summed
  // from the filtered rows (the four tiles are all row-derivable).
  const shownRecords = salesRep ? filteredRows.length : summary.records;
  const sumField = (k: string) => filteredRows.reduce((s, r) => s + (Number((r as any)[k]) || 0), 0);
  // [NET-IN-BANK NEW SALES 2026-09-16] Refunds / Disputes + Disputes Won come from the server's rows.
  // [NEW SALES BREAKDOWN 2026-09-17] Those rows now arrive individually: the picked rep narrows the
  // lists here, and the box total is the narrowed list's sum — so a box and its hover Total can never
  // disagree. null = the server could not read them ("—", no hover).
  const repOf = (x: NewSalesRefundDispute) => x.salesRep.trim().toLowerCase();
  const wantRep = salesRep.trim().toLowerCase();
  const refundItems = summary.refunds_breakdown
    ? (salesRep ? summary.refunds_breakdown.filter((x) => repOf(x) === wantRep) : summary.refunds_breakdown)
    : null;
  const wonItems = summary.won_breakdown
    ? (salesRep ? summary.won_breakdown.filter((x) => repOf(x) === wantRep) : summary.won_breakdown)
    : null;
  const round2 = (v: number) => Math.round(v * 100) / 100;
  const sumItems = (items: NewSalesRefundDispute[] | null) =>
    (items ? round2(items.reduce((s, x) => s + (Number(x.amount) || 0), 0)) : null);

  /* [NEW SALES USA/ROW 2026-09-17, user] One bar's figures. ROW = Team ROW - Sales: the payments'
     row_ twins (row_status 'ROW') and the refund/dispute rows flagged isRow (the EOM's ROW - Sales
     test). USA = everything minus ROW, so USA + ROW is always exactly what this strip showed as one
     bar. Money comes from the server summary for "All Sales Rep" and from the filtered rows for a
     picked rep, exactly as before; refunds/disputes always from the (rep-narrowed) row lists.
     Net In-Bank = Total New Sales + Disputes Won − Refunds / Disputes; unknown refunds → "—". */
  const money = (k: keyof NewSalesSummary) => (salesRep ? sumField(k as string) : Number(summary[k]) || 0);
  // [LOAD-STATES 2026-09-29, user] The boxes come from the server summary (All Sales Rep) or from the
  // rows (one rep): whichever of the two failed, its boxes say "—", never a made-up $0.00.
  const figuresFailed = salesRep ? loadFailed : summaryFailed;
  const cash = (v: number | null) => (figuresFailed || v === null ? '—' : fmtMoney(v));
  const barFigures = (scope: SalesScope) => {
    const pick = (allKey: keyof NewSalesSummary, rowKey: keyof NewSalesSummary) =>
      round2(scope === 'row' ? money(rowKey) : money(allKey) - money(rowKey));
    const inScope = (x: NewSalesRefundDispute) => !!x.isRow === (scope === 'row');
    const rItems = refundItems ? refundItems.filter(inScope) : null;
    const wItems = wonItems ? wonItems.filter(inScope) : null;
    const total = pick('total_payment_received', 'row_total_payment_received');
    const refunds = sumItems(rItems);
    const won = sumItems(wItems);
    return {
      total,
      recurring: pick('recurring_new_sale', 'row_recurring_new_sale'),
      oneTime: pick('one_time_new_sale', 'row_one_time_new_sale'),
      pia: pick('sales_pia', 'row_sales_pia'),
      refunds,
      won,
      net: refunds === null || won === null ? null : round2(total + won - refunds),
      refundItems: rItems,
      wonItems: wItems,
    };
  };

  // Client-side sort — the endpoint returns the full set (month/date desc), so we
  // re-order in place when a header is clicked.
  const sortedData = useMemo(() => {
    const rows = [...filteredRows];
    const { sortBy, sortOrder } = sort;
    const dir = sortOrder === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      const av = (a as any)[sortBy];
      const bv = (b as any)[sortBy];
      // Nulls always sort last regardless of direction.
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (sortBy === 'payment_date') {
        return (new Date(av).getTime() - new Date(bv).getTime()) * dir;
      }
      if (typeof av === 'number' || typeof bv === 'number') {
        return ((Number(av) || 0) - (Number(bv) || 0)) * dir;
      }
      return String(av).localeCompare(String(bv)) * dir;
    });
    return rows;
  }, [filteredRows, sort]);

  const columns: Column[] = baseColumns.map((col) => {
    if (col.key === 'payment_date') {
      return { ...col, render: (val: string) => {
        if (!val) return '—';
        const d = new Date(val);
        return MONTH_SHORT_DAY_YEAR_UTC_FMT.format(d);
      }};
    }
    if (col.key === 'website') {
      return { ...col, render: (val: string, row: NewSaleRow) => {
        const label = val || '—';
        const count = row.payment_count ?? 1;
        // Clubbed row → show "site (N)" with the hover breakdown; otherwise plain.
        if (count > 1 && row.merged && row.merged.length > 1) {
          return <MergedWebsiteCell label={label} count={count} merged={row.merged} />;
        }
        return label;
      }};
    }
    if (col.key === 'email' || col.key === 'account_name') {
      return { ...col, render: (val: string) => val || '—' };
    }
    if (col.key === 'sales_rep') {
      // [SALES-REP 2026-06-29] Deal owner name(s); blank (not "—") when there are none,
      // per request. Multiple names arrive comma-joined from the server.
      return { ...col, render: (val: string | null) => val || '' };
    }
    if (col.key === 'amount') {
      return { ...col, render: (val: number) => (val != null ? fmtMoney(val) : '—') };
    }
    if (col.key === 'pm_name') {
      return { ...col, render: (val: string | null, row: NewSaleRow) => {
        // Has a PM = existing/retained customer — show an "Existing client" tag
        // (neutral pill) in place of the name, mirroring the "New client" tag.
        // Hovering it reveals the PM name + placed value in a premium glass card.
        if (val) return <ExistingClientTag pmName={val} placedValue={row.placed_value} />;
        // No PM = brand-new customer (case 1). Flag it so sales can spot fresh business.
        return <CustomerPill $new={row.is_new_customer}>{row.is_new_customer ? 'New client' : '—'}</CustomerPill>;
      }};
    }
    if (col.key === 'recurring_new_sale' || col.key === 'one_time_new_sale' || col.key === 'sales_pia') {
      return { ...col, render: (val: number) => <MoneyCell value={val} /> };
    }
    if (col.key === 'total_payment_received') {
      return { ...col, render: (val: number) => <MoneyCell value={val} accent /> };
    }
    return col;
  });

  const handleSearchChange = (val: string) => {
    setSearch(val);
    // A search spans every month (months = []); clearing it snaps back to the
    // current month. Same rule as before, expressed as the multi-select array.
    setMonths(val ? [] : [getCurrentMonth()]);
    if (val) setAccount('');
    setSalesRep('');
  };

  // Extracted so both toolbar layouts (desktop inline / mobile SearchRow) reuse the
  // exact same search field. Search sits in the middle of the toolbar on desktop,
  // filling the gap between the title and the right-side filters (same as Payments).
  const searchField = (
    <SearchBar inline liquid value={search} onChange={handleSearchChange} />
  );

  return (
    // [PHONE-LAYOUT 2026-10-23] Phones: the page scrolls, so a figure's card (drawn fixed where its box was) closes.
    <Container onScroll={isMobile ? breakdownHover.close : undefined}>
      <FrozenTop>
      <Toolbar>
        <PageTitle>New Sales</PageTitle>
        {/* [MOBILE 2026-07-07] Phones: search + record count share one row
            (SearchRow); the 2 dropdowns drop to their own row (Filters grid).
            Desktop: search fills the middle, count sits with the filters. */}
        {isMobile ? (
          <SearchRow>
            {searchField}
            <RecordCount>{loadFailed && data.length === 0 ? 'Not loaded' : `${shownRecords.toLocaleString()} records fetched`}</RecordCount>
          </SearchRow>
        ) : searchField}
        <Filters>
          {!isMobile && <RecordCount>{loadFailed && data.length === 0 ? 'Not loaded' : `${shownRecords.toLocaleString()} records fetched`}</RecordCount>}
          {/* [SINGLE-MONTH 2026-07-28] One month at a time (multi-select withdrawn).
              State stays a string[] of length 1 so nothing downstream changes. */}
          <FilterSelect liquid value={months[0] || ''} onChange={(e) => { setMonths([e.target.value]); setSalesRep(''); }}>
            {Array.from(new Set([...months, ...filters.months]))
              .sort((a, b) => b.localeCompare(a))
              .map((m) => (
                <option key={m} value={m}>{formatMonthLabel(m)}</option>
              ))}
          </FilterSelect>
          {/* [SALES-REP-FILTER 2026-07-02] Filter the table + summary by deal owner. */}
          <FilterSelect liquid value={salesRep} onChange={(e) => setSalesRep(e.target.value)}>
            <option value="">All Sales Rep</option>
            {repList.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </FilterSelect>
        </Filters>
      </Toolbar>

      <SummarySection>
        {/* [NEW SALES USA/ROW 2026-09-17, user] Two stacked bars — USA, then ROW — same seven boxes. */}
        {loading ? (
          <SummaryStack>
            {SALES_SCOPES.map((scope) => (
              <SummaryGrid key={scope} $cols={7} $labelCol>
                <ScopeCell $scope={scope}>{SALES_SCOPE_LABEL[scope]}</ScopeCell>
                {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                  <SummaryCell key={i}>
                    <SkeletonBox $w="60%" $h="8px" />
                    <SkeletonBox $w="80%" $h="16px" $mt="9px" />
                  </SummaryCell>
                ))}
              </SummaryGrid>
            ))}
          </SummaryStack>
        ) : (
        <SummaryStack onScroll={isMobile ? breakdownHover.close : undefined /* [PHONE-LAYOUT 2026-10-23] the bars swipe */}>
          {SALES_SCOPES.map((scope) => {
            const f = barFigures(scope);
            const where = scope === 'row' ? ' · ROW' : ' · USA';
            return (
              <SummaryGrid key={scope} $cols={7} $labelCol>
                <ScopeCell $scope={scope}>{SALES_SCOPE_LABEL[scope]}</ScopeCell>
                <SummaryCell>
                  <SummaryLabel>Total New Sales</SummaryLabel>
                  <SummaryValue $accent>{cash(f.total)}</SummaryValue>
                </SummaryCell>
                <SummaryCell>
                  <SummaryLabel>Recurring New Sale</SummaryLabel>
                  <SummaryValue>{cash(f.recurring)}</SummaryValue>
                </SummaryCell>
                <SummaryCell>
                  <SummaryLabel>One-Time New Sale</SummaryLabel>
                  <SummaryValue>{cash(f.oneTime)}</SummaryValue>
                </SummaryCell>
                <SummaryCell>
                  <SummaryLabel>Sales PIA</SummaryLabel>
                  <SummaryValue>{cash(f.pia)}</SummaryValue>
                </SummaryCell>
                {/* [NET-IN-BANK NEW SALES 2026-09-16, user] Three boxes after Sales PIA. */}
                {/* [NEW SALES BREAKDOWN 2026-09-17, user] Hover (0.6 s) → the rows behind the figure, like
                    My Inventory. A box with no rows opens nothing. Each bar lists only its own rows. */}
                <SummaryCell {...breakdownHover.bind(() => (f.refundItems ? {
                  items: f.refundItems.map((x) => ({ website: x.website, person: x.salesRep, amount: x.amount, type: x.type })),
                  tone: 'red' as const,
                  showType: true,
                  countLabel: `${f.refundItems.length} refund${f.refundItems.length !== 1 ? 's' : ''} / dispute${f.refundItems.length !== 1 ? 's' : ''}${where}`,
                } : null))}>
                  <SummaryLabel>Refunds / Disputes</SummaryLabel>
                  <SummaryValue $tone="red">{cash(f.refunds)}</SummaryValue>
                </SummaryCell>
                <SummaryCell {...breakdownHover.bind(() => (f.wonItems ? {
                  items: f.wonItems.map((x) => ({ website: x.website, person: x.salesRep, amount: x.amount, type: x.type })),
                  tone: 'green' as const,
                  showType: false,
                  countLabel: `${f.wonItems.length} dispute${f.wonItems.length !== 1 ? 's' : ''} won${where}`,
                } : null))}>
                  <SummaryLabel>Disputes Won</SummaryLabel>
                  <SummaryValue $tone="green">{cash(f.won)}</SummaryValue>
                </SummaryCell>
                <SummaryCell>
                  <SummaryLabel>Net In-Bank</SummaryLabel>
                  <SummaryValue $tone={f.net !== null && f.net < 0 && !figuresFailed ? 'red' : undefined}>{cash(f.net)}</SummaryValue>
                </SummaryCell>
              </SummaryGrid>
            );
          })}
        </SummaryStack>
        )}
        <RefundBreakdownCard
          tip={breakdownHover.tip}
          format={fmtMoney}
          onEnter={breakdownHover.cancelHide}
          onLeave={breakdownHover.close}
        />
      </SummarySection>
      </FrozenTop>

      <TableScroll>
        <DataTable
          parentScroll
          columns={columns}
          data={sortedData}
          sort={sort}
          onSort={handleSort}
          loading={loading}
          // [MOBILE 2026-07-07] Wide New Sales grid — keep the SAME desktop table on
          // phones (no card view); it scrolls horizontally inside TableScroll so the
          // full grid reads exactly like the website. Mirrors Refunds/Disputes.
          disableMobileCards
          // [COL-RESIZE-ALL 2026-09-29, user] Drag a header's edge to resize (same as Payments).
          resizeStorageKey={`dt-new-sales-colw:${user?.id ?? 'anon'}`}
          // [LOAD-STATES 2026-09-29, user] countdown card on a failed load; a clear line when empty.
          highlight={search} // [SEARCH-HIGHLIGHT 2026-09-29]
          loadFailed={loadFailed && data.length === 0 ? { what: 'new sales', onRetry: () => { loadSummaryRef.current(); return load(); } } : undefined}
          empty={search
            ? { icon: 'search', title: `No new sales match “${search}”`, hint: 'Check the spelling, or clear the search to see every new sale.', action: { label: 'Clear search', onClick: () => handleSearchChange('') } }
            : salesRep
              ? { icon: 'search', title: `No new sales for ${salesRep}${months.length === 1 ? ` in ${formatMonthLabel(months[0])}` : ''}`, action: { label: 'Show all sales reps', onClick: () => setSalesRep('') } }
              : { title: `No new sales${months.length === 1 ? ` in ${formatMonthLabel(months[0])}` : ''}`, hint: 'Pick another month or account to see more.' }}
        />
      </TableScroll>
    </Container>
  );
};

export default NewSalesPage;
